import { Employee, Absence } from '../types';

export type CompanyNotificationPayload = {
  title: string;
  message: string;
  type: 'add' | 'delete' | 'info';
};

type DatedReasonRecord = { date: string; reason: string };
type DatedDescriptionRecord = { date: string; description: string };

/**
 * Normaliza data para comparação (YYYY-MM-DD).
 * @param value - Data ISO ou date-only
 * @returns Data sem horário
 */
function normalizeDateKey(value: string): string {
  return value.includes('T') ? value.split('T')[0] : value;
}

/**
 * Chave estável para comparar registros de falta/atraso/saída.
 * @param record - Registro com data e motivo
 * @returns Chave única para diff
 */
function reasonRecordKey(record: DatedReasonRecord): string {
  return `${normalizeDateKey(record.date)}|${record.reason.trim()}`;
}

/**
 * Chave estável para comparar situações.
 * @param record - Registro com data e descrição
 * @returns Chave única para diff
 */
function situationRecordKey(record: DatedDescriptionRecord): string {
  return `${normalizeDateKey(record.date)}|${record.description.trim()}`;
}

/**
 * Retorna itens presentes em `after` mas ausentes em `before`.
 * @param before - Estado anterior
 * @param after - Estado novo
 * @param keyFn - Função de chave estável
 * @returns Registros adicionados
 */
function findAdded<T>(before: T[], after: T[], keyFn: (item: T) => string): T[] {
  const beforeKeys = new Set(before.map(keyFn));
  return after.filter((item) => !beforeKeys.has(keyFn(item)));
}

/**
 * Retorna itens presentes em `before` mas ausentes em `after`.
 * @param before - Estado anterior
 * @param after - Estado novo
 * @param keyFn - Função de chave estável
 * @returns Registros removidos
 */
function findRemoved<T>(before: T[], after: T[], keyFn: (item: T) => string): T[] {
  const afterKeys = new Set(after.map(keyFn));
  return before.filter((item) => !afterKeys.has(keyFn(item)));
}

/**
 * Verifica se campos principais do colaborador mudaram (exceto listas de eventos).
 * @param before - Colaborador antes da edição
 * @param after - Colaborador após a edição
 * @returns true se houve alteração em dados cadastrais
 */
function hasEmployeeFieldChanges(before: Employee, after: Employee): boolean {
  return (
    before.name !== after.name ||
    before.role !== after.role ||
    before.status !== after.status ||
    (before.code || '') !== (after.code || '') ||
    (before.birthDate || '') !== (after.birthDate || '') ||
    (before.hiringDate || '') !== (after.hiringDate || '') ||
    (before.whatsapp || '') !== (after.whatsapp || '') ||
    (before.costCenterCode || '') !== (after.costCenterCode || '') ||
    (before.costCenterDescription || '') !== (after.costCenterDescription || '')
  );
}

/**
 * Formata data para exibição em mensagens de notificação.
 * @param date - Data ISO ou date-only
 * @returns Data local pt-BR
 */
function formatNotificationDate(date: string): string {
  const key = normalizeDateKey(date);
  const [year, month, day] = key.split('-').map(Number);
  if (!year || !month || !day) return key;
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
}

/**
 * Monta payloads de notificação ao cadastrar novo colaborador.
 * @param employeeName - Nome do colaborador
 * @param sectorTitle - Título do setor
 * @returns Lista de notificações a propagar
 */
export function buildNewEmployeeNotifications(
  employeeName: string,
  sectorTitle: string,
): CompanyNotificationPayload[] {
  return [
    {
      title: 'Novo colaborador',
      message: `${employeeName} foi cadastrado no setor "${sectorTitle}".`,
      type: 'add',
    },
  ];
}

/**
 * Monta payload ao remover colaborador.
 * @param employeeName - Nome do colaborador
 * @param sectorTitle - Título do setor
 * @returns Notificação de exclusão
 */
export function buildDeletedEmployeeNotification(
  employeeName: string,
  sectorTitle: string,
): CompanyNotificationPayload {
  return {
    title: 'Colaborador removido',
    message: `${employeeName} foi removido do setor "${sectorTitle}".`,
    type: 'delete',
  };
}

/**
 * Monta payload quando colaborador é movido entre setores (drag ou edição).
 * @param employeeName - Nome do colaborador
 * @param fromSector - Setor de origem
 * @param toSector - Setor de destino
 * @returns Notificação de edição
 */
export function buildEmployeeMoveNotification(
  employeeName: string,
  fromSector: string,
  toSector: string,
): CompanyNotificationPayload {
  return {
    title: 'Colaborador editado',
    message: `${employeeName} foi movido de "${fromSector}" para "${toSector}".`,
    type: 'info',
  };
}

/**
 * Monta payloads ao editar colaborador — diff de cadastro e registros vinculados.
 * @param before - Estado anterior
 * @param after - Estado novo
 * @param sectorTitle - Setor atual
 * @returns Lista de notificações a propagar
 */
export function buildEmployeeEditNotifications(
  before: Employee,
  after: Employee,
  sectorTitle: string,
): CompanyNotificationPayload[] {
  const payloads: CompanyNotificationPayload[] = [];
  const name = after.name || before.name;

  if (hasEmployeeFieldChanges(before, after)) {
    payloads.push({
      title: 'Colaborador editado',
      message: `Os dados de ${name} no setor "${sectorTitle}" foram atualizados.`,
      type: 'info',
    });
  }

  const beforeAbsences = before.absences ?? [];
  const afterAbsences = after.absences ?? [];
  for (const absence of findAdded(beforeAbsences, afterAbsences, reasonRecordKey)) {
    payloads.push({
      title: 'Falta adicionada',
      message: `Falta registrada para ${name} em ${formatNotificationDate(absence.date)}.`,
      type: 'add',
    });
  }
  for (const absence of findRemoved(beforeAbsences, afterAbsences, reasonRecordKey)) {
    payloads.push({
      title: 'Falta removida',
      message: `Falta removida de ${name} (${formatNotificationDate(absence.date)}).`,
      type: 'delete',
    });
  }

  const beforeSituations = before.situations ?? [];
  const afterSituations = after.situations ?? [];
  for (const situation of findAdded(beforeSituations, afterSituations, situationRecordKey)) {
    payloads.push({
      title: 'Situação adicionada',
      message: `Situação registrada para ${name} em ${formatNotificationDate(situation.date)}.`,
      type: 'add',
    });
  }
  for (const situation of findRemoved(beforeSituations, afterSituations, situationRecordKey)) {
    payloads.push({
      title: 'Situação removida',
      message: `Situação removida de ${name} (${formatNotificationDate(situation.date)}).`,
      type: 'delete',
    });
  }

  const beforeDelays = before.delays ?? [];
  const afterDelays = after.delays ?? [];
  for (const delay of findAdded(beforeDelays, afterDelays, reasonRecordKey)) {
    payloads.push({
      title: 'Atraso adicionado',
      message: `Atraso registrado para ${name} em ${formatNotificationDate(delay.date)}.`,
      type: 'add',
    });
  }
  for (const delay of findRemoved(beforeDelays, afterDelays, reasonRecordKey)) {
    payloads.push({
      title: 'Atraso removido',
      message: `Atraso removido de ${name} (${formatNotificationDate(delay.date)}).`,
      type: 'delete',
    });
  }

  const beforeEarly = before.earlyDepartures ?? [];
  const afterEarly = after.earlyDepartures ?? [];
  for (const early of findAdded(beforeEarly, afterEarly, reasonRecordKey)) {
    payloads.push({
      title: 'Saída antecipada adicionada',
      message: `Saída antecipada registrada para ${name} em ${formatNotificationDate(early.date)}.`,
      type: 'add',
    });
  }
  for (const early of findRemoved(beforeEarly, afterEarly, reasonRecordKey)) {
    payloads.push({
      title: 'Saída antecipada removida',
      message: `Saída antecipada removida de ${name} (${formatNotificationDate(early.date)}).`,
      type: 'delete',
    });
  }

  return payloads;
}

/**
 * Monta payload quando motivo de falta é editado no modal de faltas.
 * @param employeeName - Nome do colaborador
 * @param absence - Falta editada
 * @returns Notificação de edição
 */
export function buildAbsenceReasonEditNotification(
  employeeName: string,
  absence: Absence,
): CompanyNotificationPayload {
  return {
    title: 'Colaborador editado',
    message: `Falta de ${employeeName} em ${formatNotificationDate(absence.date)} foi atualizada.`,
    type: 'info',
  };
}
